import { el } from "../utils/dom.ts";
import { apiGet, getLatest, type LatestEntry } from "../services/api-client.ts";

interface Tile {
  label: string;
  value: string;
  delta?: string;
  deltaClass?: string;
  tab: string; // hash to link to
}

// Cross-axis stat tiles for the Highlight tab. Each group loads independently; a failed
// fetch renders "—" rather than breaking the row. Every tile links to its axis tab.
export class StatTilesPanel {
  readonly el: HTMLElement;

  constructor() {
    this.el = el("div", { className: "stat-tiles" });
    this.renderSkeleton();
  }

  private renderSkeleton(): void {
    this.el.replaceChildren(
      ...["Vàng QT", "Vàng SJC", "Dầu Brent", "USD/VND", "CPI", "Chính sách", "Sắc thái"].map((l) =>
        el("div", { className: "stat-tile loading" }, [
          el("div", { className: "stat-label" }, [l]),
          el("div", { className: "stat-value" }, ["…"]),
        ]),
      ),
    );
  }

  async load(): Promise<void> {
    const tiles: Tile[] = [];
    // Prices (one call for all).
    try {
      const { series } = await getLatest();
      const find = (s: string) => series.find((x) => x.series === s);
      tiles.push(priceTile("Vàng QT", find("xau-usd"), "usd"));
      tiles.push(priceTile("Vàng SJC", find("sjc-bar"), "vnd-gold"));
      tiles.push(priceTile("Dầu Brent", find("brent"), "usd"));
      tiles.push(priceTile("USD/VND", find("usd-vnd"), "vnd-fx"));
    } catch {
      tiles.push(errTile("Giá", "kinh-te"));
    }
    // CPI (macro).
    try {
      const cpi = await apiGet<{ points: Array<{ period: string; value: number }> }>(
        "/api/v1/macro/series?indicator=cpi-all-yoy",
      );
      const last = cpi.points.at(-1);
      tiles.push({
        label: "CPI (YoY)",
        value: last ? `${last.value}%` : "—",
        tab: "kinh-te",
      });
    } catch {
      tiles.push(errTile("CPI", "kinh-te"));
    }
    // Policy count.
    try {
      const policy = await apiGet<{ items: unknown[] }>("/api/v1/news?limit=50&category=policy");
      tiles.push({ label: "Tin chính sách", value: `${policy.items.length}`, tab: "chinh-tri-xa-hoi" });
    } catch {
      tiles.push(errTile("Chính sách", "chinh-tri-xa-hoi"));
    }
    // Sentiment 7d (may be empty while accumulating).
    try {
      const s = await apiGet<{ points: Array<{ avg: number }> }>(
        "/api/v1/sentiment/series?days=7",
      );
      const avg = s.points.length
        ? s.points.reduce((a, p) => a + p.avg, 0) / s.points.length
        : null;
      tiles.push({
        label: "Sắc thái 7 ngày",
        value: avg === null ? "—" : avg.toFixed(2),
        tab: "chinh-tri-xa-hoi",
      });
    } catch {
      tiles.push(errTile("Sắc thái", "chinh-tri-xa-hoi"));
    }

    this.el.replaceChildren(
      ...tiles.map((t) =>
        el(
          "a",
          { className: "stat-tile", href: `#${t.tab}` },
          [
            el("div", { className: "stat-label" }, [t.label]),
            el("div", { className: "stat-value" }, [t.value]),
            t.delta ? el("div", { className: `stat-delta ${t.deltaClass ?? ""}` }, [t.delta]) : "",
          ],
        ),
      ),
    );
  }
}

function priceTile(label: string, entry: LatestEntry | undefined, fmt: string): Tile {
  if (!entry) return errTile(label, "kinh-te");
  const value =
    entry.kind === "mid" ? entry.price : entry.sell;
  const delta = entry.delta24h;
  return {
    label,
    value: formatValue(value, fmt),
    delta: delta ? `${delta.pct >= 0 ? "▲" : "▼"} ${Math.abs(delta.pct).toFixed(2)}%` : undefined,
    deltaClass: delta ? (delta.pct >= 0 ? "up" : "down") : "",
    tab: "kinh-te",
  };
}

function formatValue(v: number, fmt: string): string {
  if (fmt === "vnd-gold") return `${(v / 1_000_000).toFixed(1)} tr`;
  if (fmt === "vnd-fx") return v.toLocaleString("vi-VN");
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function errTile(label: string, tab: string): Tile {
  return { label, value: "—", tab };
}
